import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaQuestionCircle, FaArrowRight, FaPhone, FaEnvelope } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import usePageTitle from '../hooks/usePageTitle';
import './FAQ.css';

const FAQ = () => {
  usePageTitle('Frequently Asked Questions');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // FAQ categories and questions
  const faqCategories = [
    {
      id: 'general',
      title: 'General Questions',
      questions: [
        {
          id: 'what-is-hoc',
          question: 'What is House of Canvas?',
          answer: 'House of Canvas is a leading tent manufacturer in East Africa, established in 2010. We specialize in designing and manufacturing premium quality tents for various applications including events, safaris, military, and commercial purposes. With operations in Tanzania and Kenya, we serve clients across East Africa and beyond.'
        },
        {
          id: 'locations',
          question: 'Where are you located?',
          answer: 'Our main office is located at Nelson Mandela Road, Arusha, Tanzania. We also have operations in Kenya to serve the wider East African market.'
        },
        {
          id: 'service-areas',
          question: 'Do you serve clients outside Tanzania and Kenya?',
          answer: 'Yes, while our main operations are in Tanzania and Kenya, we serve clients across East Africa and can arrange shipping and installation for international clients as well.'
        }
      ]
    },
    {
      id: 'products',
      title: 'Products & Services',
      questions: [
        {
          id: 'tent-types',
          question: 'What types of tents do you manufacture?',
          answer: 'We manufacture a wide range of tents including stretch tents, frame tents, safari luxury tents, military tents, pole tents, marquee tents, and custom designs for special events and purposes. Each type is designed with specific applications in mind and can be customized to meet your requirements.'
        },
        {
          id: 'materials',
          question: 'What materials do you use for your tents?',
          answer: 'We use high-quality, durable materials for our tents including PVC, polyester with PU coating, canvas, and other weather-resistant fabrics. All our materials are selected for their durability, weather resistance, and longevity. We ensure our materials meet international standards for safety and quality.'
        },
        {
          id: 'customization',
          question: 'Can you customize tents with branding?',
          answer: 'Absolutely! We offer customization services including branding, logos, and custom colors. Our design team will work with you to incorporate your branding elements into the tent design to create a unique and professional look that represents your brand.'
        },
        {
          id: 'services',
          question: 'What services do you offer besides tent manufacturing?',
          answer: 'In addition to tent manufacturing, we offer tent rentals, consultations, furniture solutions, and electrical wiring services. Our comprehensive approach ensures that we can provide end-to-end solutions for all your tent-related needs.'
        }
      ]
    },
    {
      id: 'ordering',
      title: 'Ordering & Delivery',
      questions: [
        {
          id: 'lead-time',
          question: 'What is the lead time for custom tent orders?',
          answer: 'Lead times vary depending on the complexity and size of the order. Typically, our custom tent orders take 2-4 weeks to complete. Please contact us for a more accurate timeline for your specific requirements.'
        },
        {
          id: 'shipping',
          question: 'Do you ship internationally?',
          answer: 'Yes, we ship our products internationally. Shipping costs and delivery times vary depending on the destination. Please contact us for shipping details to your location.'
        },
        {
          id: 'payment',
          question: 'What payment methods do you accept?',
          answer: 'We accept various payment methods including bank transfers, mobile money, and credit cards. For large orders, we typically require a deposit upfront with the balance due before delivery. Please contact our sales team for specific payment arrangements.'
        },
        {
          id: 'minimum-order',
          question: 'Is there a minimum order quantity?',
          answer: 'For standard products, there is no minimum order quantity. However, for custom-designed tents, there may be minimum requirements depending on the complexity of the design. Please contact us to discuss your specific needs.'
        }
      ]
    },
    {
      id: 'maintenance',
      title: 'Maintenance & Support',
      questions: [
        {
          id: 'warranty',
          question: 'Do your tents come with a warranty?',
          answer: 'Yes, all our tents come with a warranty that covers manufacturing defects. The warranty period varies depending on the type of tent and its intended use. Please contact us for specific warranty information for your purchase.'
        },
        {
          id: 'maintenance',
          question: 'How do I maintain my tent?',
          answer: 'Proper maintenance is essential for extending the life of your tent. We recommend regular cleaning with mild soap and water, ensuring the tent is completely dry before storage, and avoiding sharp objects near the tent fabric. We provide detailed maintenance instructions with each purchase.'
        },
        {
          id: 'repairs',
          question: 'Do you offer repair services?',
          answer: 'Yes, we offer repair services for our tents. If your tent is damaged, please contact us to arrange for an assessment and repair. In many cases, minor repairs can be done on-site, while more significant damage may require the tent to be returned to our workshop.'
        },
        {
          id: 'support',
          question: 'What kind of after-sales support do you provide?',
          answer: 'We provide comprehensive after-sales support including maintenance advice, repair services, and replacement parts. Our team is available to answer any questions you may have about your tent and to help resolve any issues that may arise.'
        }
      ]
    },
    {
      id: 'rentals',
      title: 'Rentals & Events',
      questions: [
        {
          id: 'rental-process',
          question: 'How does your tent rental process work?',
          answer: 'Our rental process begins with a consultation to understand your needs. We then provide a quote, schedule delivery and setup, and after your event, we handle the dismantling and removal. We ensure a smooth experience from start to finish.'
        },
        {
          id: 'rental-duration',
          question: 'What is the minimum rental duration?',
          answer: 'The minimum rental duration is typically one day, but this can vary depending on the type of tent and the scale of the event. For larger events that require significant setup time, we may recommend a longer rental period.'
        },
        {
          id: 'event-planning',
          question: 'Can you help with event planning?',
          answer: 'While our primary focus is on providing tent solutions, we can offer advice on tent placement, size requirements, and layout based on your event needs. We also work with several event planning partners whom we can recommend for comprehensive event planning services.'
        },
        {
          id: 'weather',
          question: 'How do your tents perform in different weather conditions?',
          answer: 'Our tents are designed to withstand various weather conditions including rain, wind, and sun. The specific weather resistance depends on the type of tent. We can advise on the best tent option based on the expected weather conditions for your event or application.'
        }
      ]
    }
  ];

  return (
    <div className="faq-page">
      {/* Hero Section */}
      <section className="faq-hero">
        <div className="container">
          <motion.div 
            className="faq-hero-content"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1>Frequently Asked Questions</h1>
            <p>Find answers to common questions about our products and services</p>
          </motion.div>
        </div>
      </section>
      
      {/* FAQ Introduction */}
      <section className="faq-intro">
        <div className="container">
          <motion.div 
            className="intro-content"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2>How Can We Help You?</h2>
            <p>
              We've compiled answers to the most common questions we receive about our tent solutions, 
              services, and processes. If you can't find the answer you're looking for, please don't 
              hesitate to contact our team directly.
            </p>
            <div className="contact-options">
              <a href="tel:+255765603466" className="contact-option">
                <FaPhone /> Call Us: +255 765 603 466
              </a>
              <a href="mailto:info@houseofcanvas.net" className="contact-option">
                <FaEnvelope /> Email: info@houseofcanvas.net
              </a>
            </div>
          </motion.div>
        </div>
      </section>
      
      {/* FAQ Categories */}
      <section className="faq-categories">
        <div className="container">
          <div className="categories-grid">
            {faqCategories.map((category, index) => (
              <motion.a 
                key={category.id} 
                href={`#${category.id}`}
                className="category-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -10, boxShadow: "0 15px 30px rgba(0, 0, 0, 0.1)" }}
              >
                <FaQuestionCircle className="category-icon" />
                <h3>{category.title}</h3>
                <p>{category.questions.length} questions</p>
              </motion.a>
            ))}
          </div>
        </div>
      </section>
      
      {/* FAQ Content */}
      <section className="faq-content">
        <div className="container">
          {faqCategories.map((category, categoryIndex) => (
            <motion.div 
              key={category.id} 
              id={category.id}
              className="faq-category"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2>{category.title}</h2>
              <div className="faq-questions">
                {category.questions.map((faq, faqIndex) => (
                  <motion.div 
                    key={faq.id} 
                    className="faq-item"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: faqIndex * 0.1 }}
                    viewport={{ once: true }}
                  >
                    <h3 id={faq.id}>{faq.question}</h3>
                    <div className="faq-answer">
                      <p>{faq.answer}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="faq-cta">
        <div className="container">
          <motion.div 
            className="cta-content"
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2>Still have questions?</h2>
            <p>Our team is ready to assist you with any additional questions or to discuss your specific requirements.</p>
            <Link to="/contact" className="cta-button">
              Contact Us <FaArrowRight style={{ marginLeft: '8px' }} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default FAQ; 